import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { DatePipe, DecimalPipe } from '@angular/common';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';

import { AiCallProduct } from '../../core/admin/ai-call.service';
import { AiComparisonService } from '../../core/admin/ai-comparison.service';
import { AiComparisonRow } from '../../core/admin/ai-comparison.types';
import { AiEvaluationDetailResponse } from '../../core/admin/ai-evaluations.types';
import { AiMarkComponent } from '../ai-mark/ai-mark';
import { ModalComponent } from '../modal/modal';
import { ZarPipe } from '../pipes/zar.pipe';

export interface AiRunDialogData {
  product: AiCallProduct;
  targetId: string;
  row: AiComparisonRow;
}

/**
 * One run: what was sent to the model and what it came back with (Ops-only, N4).
 * Leads with the answer in words; the bodies follow, folded, exactly as sent and
 * received. Fetched when opened, because the bodies are large and live in S3, and
 * their shape is the provider's, so nothing here reads fields off them.
 */
@Component({
  selector: 'app-ai-run-dialog',
  imports: [DatePipe, DecimalPipe, AiMarkComponent, ModalComponent, ZarPipe],
  template: `
    <app-modal [heading]="'Run on ' + data.row.model" [error]="error()">
      @if (data.row.error) {
        <p class="ai-run__error">{{ data.row.error }}</p>
      }
      @if (detail()) {
        <dl class="ai-run__facts" data-testid="ai-run-facts">
          <div>
            <dt>When</dt>
            <dd>{{ data.row.created_at | date: 'd MMM y, HH:mm' }}</dd>
          </div>
          <div>
            <dt>Cost</dt>
            <dd>{{ data.row.cost_zar | zar }}</dd>
          </div>
          <div>
            <dt>Tokens in / cache read / cache written / out</dt>
            <dd>
              {{ data.row.input_tokens | number }} /
              {{ data.row.cache_read_input_tokens | number }} /
              {{ data.row.cache_creation_input_tokens | number }} /
              {{ data.row.output_tokens | number }}
            </dd>
          </div>
        </dl>
        @if (data.row.engine_outcome.reason; as reason) {
          <h3 class="ai-run__head">What the engine made of it</h3>
          <p>{{ reason }}</p>
        }
        @if (data.row.summary && !data.row.is_stub) {
          <h3 class="ai-run__head">
            <span>What the model said</span>
            <app-ai-mark />
          </h3>
          <p>{{ data.row.summary }}</p>
        }
        @if (missing().length) {
          <h3 class="ai-run__head">
            <span>What it said is missing</span>
            <app-ai-mark />
          </h3>
          <ul>
            @for (item of missing(); track item) {
              <li>{{ item }}</li>
            }
          </ul>
        }
        <details class="ai-run__raw">
          <summary>
            <span>What came back, in full</span>
            <app-ai-mark />
          </summary>
          @if (returned()) {
            <pre class="ai-run__body" data-testid="ai-run-response">{{ returned() }}</pre>
          } @else {
            <p class="modal-note">No response body is stored for this run.</p>
          }
        </details>
        <details class="ai-run__raw">
          <summary>What was sent, in full</summary>
          @if (sent()) {
            <p class="modal-note">
              Document files are left out here. The model received them in full.
            </p>
            <pre class="ai-run__body" data-testid="ai-run-request">{{ sent() }}</pre>
          } @else {
            <p class="modal-note">No request body is stored for this run.</p>
          }
        </details>
      } @else if (!error()) {
        <p class="modal-note">Loading…</p>
      }
    </app-modal>
  `,
  styles: `
    .ai-run__head {
      display: flex;
      align-items: center;
      gap: var(--space-2);
      margin: var(--space-4) 0 var(--space-2);
      font-size: var(--font-size-sm);
      font-weight: 600;
    }

    .ai-run__body {
      max-height: 24rem;
      margin: 0;
      padding: var(--space-3);
      overflow: auto;
      font-size: var(--font-size-xs);
      white-space: pre-wrap;
      overflow-wrap: anywhere;
      background: var(--muted);
      border-radius: var(--radius-sm);
    }

    .ai-run__facts {
      display: flex;
      flex-wrap: wrap;
      gap: var(--space-4);
      margin: 0;
      font-size: var(--font-size-sm);

      dt {
        color: var(--muted-foreground);
      }

      dd {
        margin: 0;
      }
    }

    .ai-run__raw {
      margin-top: var(--space-3);

      summary {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        padding-block: var(--space-2);
        font-size: var(--font-size-sm);
        font-weight: 600;
        cursor: pointer;
      }
    }

    .ai-run__error {
      color: var(--color-error-text);
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiRunDialogComponent implements OnInit {
  protected readonly data = inject<AiRunDialogData>(MAT_DIALOG_DATA);
  private readonly service = inject(AiComparisonService);

  protected readonly detail = signal<AiEvaluationDetailResponse | null>(null);
  protected readonly error = signal<string | null>(null);
  protected readonly sent = computed(() => pretty(this.detail()?.request_body));
  /** Our own schema's field, so reading it is safe whichever provider answered. */
  protected readonly missing = computed<string[]>(() => {
    const items = this.detail()?.evaluation.payload?.['missing_items'];
    return Array.isArray(items) ? items.map(String) : [];
  });
  /** The stored response; the parsed answer where no body was kept. */
  protected readonly returned = computed(() => {
    const detail = this.detail();
    return pretty(detail?.response_body ?? detail?.evaluation.payload);
  });

  ngOnInit(): void {
    this.service.call(this.data.product, this.data.targetId, this.data.row.short_id).subscribe({
      next: (detail) => this.detail.set(detail),
      error: () => this.error.set("Couldn't load this run. Close this and try again."),
    });
  }
}

function pretty(value: unknown): string {
  return value ? JSON.stringify(value, null, 2) : '';
}
